import { Badge, Button, type ButtonProps, Tooltip } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';

export type GameButtonVariant =
  | 'primary'
  | 'warning'
  | 'destructive'
  | 'ghost'
  | 'active';
type GameButtonSize = 'sm' | 'md' | 'lg';

export interface GameButtonProps
  extends Omit<ButtonProps, 'variant' | 'color' | 'size' | 'children'> {
  variant?: GameButtonVariant;
  size?: GameButtonSize;
  loading?: boolean;
  loadingText?: string;
  badgeContent?: ReactNode;
  /**
   * ホバー時に表示する説明。
   * ネイティブの title 属性はブラウザごとに表示条件が異なり、disabled 時は
   * pointer-events が無効になるため表示されない。ラベルを持たないアイコン
   * ボタンなど、説明が確実に出る必要がある場合はこちらを使う。
   */
  tooltip?: string;
  children: ReactNode;
}

const sizeStyles: Record<
  GameButtonSize,
  { height: number; fontSize: number; px: number; minWidth: number }
> = {
  sm: { height: 36, fontSize: 13, px: 1.5, minWidth: 80 },
  md: { height: 42, fontSize: 14, px: 2, minWidth: 100 },
  lg: { height: 48, fontSize: 16, px: 2.5, minWidth: 120 },
};

/** 天面のハイライト。押せる厚みを出すためのもので、全 variant 共通 */
const INSET_HIGHLIGHT = 'inset 1px 2px 0px 0px rgba(255,255,255,0.15)';

/**
 * 影は variant ごとに「自分の背景色より暗い同系色」を敷く。
 * 一律の黒だと、明るいボタンや透明なボタンの下で色が合わない。
 */
const DEFAULT_SHADOW = `0px 3px 0px 0px rgba(0,0,0,0.5), ${INSET_HIGHLIGHT}`;

const variantStyles: Record<
  GameButtonVariant,
  {
    bg: string;
    color: string;
    hoverBg: string;
    border?: string;
    hoverBorder?: string;
    shadow?: string;
    borderRadius?: number | string;
    /** サイズ既定の左右パディングを上書きする */
    px?: number;
  }
> = {
  primary: {
    bg: '#4d5e76',
    color: '#ffffff',
    hoverBg: '#5d6e86',
    shadow: `0px 3px 0px 0px #374455, ${INSET_HIGHLIGHT}`,
  },
  warning: {
    bg: '#FFD34E',
    color: '#323349',
    hoverBg: '#f0c33a',
    border: '2px solid #323349',
    borderRadius: '8px',
    // ゲームのアクションはフッターに複数並ぶので、左右は詰めておく
    px: 1,
    shadow:
      '0px 3px 0px -1px rgba(0,0,0,0.7), inset 1px 2px 0px 1px rgba(255,255,255,1)',
  },
  destructive: {
    bg: '#bf360c',
    color: '#ffffff',
    hoverBg: '#a82e09',
    shadow: `0px 3px 0px 0px #8a2709, ${INSET_HIGHLIGHT}`,
  },
  ghost: {
    bg: 'transparent',
    color: '#555555',
    hoverBg: 'rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.23)',
    hoverBorder: '1px solid rgba(0, 0, 0, 0.5)',
    // 背景が透明なので、下に影を敷くと浮いた黒帯に見えるだけ
    shadow: 'none',
  },
  active: {
    bg: '#009688',
    color: '#ffffff',
    hoverBg: '#00897b',
    shadow: `0px 3px 0px 0px #006c62, ${INSET_HIGHLIGHT}`,
  },
};

export const GameButton = forwardRef<HTMLButtonElement, GameButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText = '処理中...',
      badgeContent,
      tooltip,
      disabled,
      children,
      sx,
      ...rest
    },
    ref,
  ) => {
    const s = sizeStyles[size];
    const v = variantStyles[variant];

    const button = (
      <Button
        ref={ref}
        disabled={disabled || loading}
        variant='contained'
        disableElevation
        {...rest}
        sx={{
          height: s.height,
          minWidth: s.minWidth,
          px: v.px ?? s.px,
          fontSize: s.fontSize,
          fontWeight: 500,
          borderRadius: v.borderRadius ?? 1.5,
          textTransform: 'none',
          boxShadow: v.shadow ?? DEFAULT_SHADOW,
          backgroundColor: v.bg,
          color: v.color,
          border: v.border ?? 'none',
          '&:hover': {
            backgroundColor: v.hoverBg,
            border: v.hoverBorder ?? v.border ?? 'none',
            boxShadow: v.shadow ?? DEFAULT_SHADOW,
          },
          '&.Mui-disabled': {
            backgroundColor: v.bg,
            color: v.color,
            border: v.border ?? 'none',
            opacity: 0.5,
          },
          ...sx,
        }}
      >
        {loading ? loadingText : children}
      </Button>
    );

    const content =
      badgeContent === undefined ||
      badgeContent === null ||
      badgeContent === false ? (
        button
      ) : (
        <Badge badgeContent={badgeContent} color='info'>
          {button}
        </Badge>
      );

    if (!tooltip) {
      return content;
    }

    return (
      <Tooltip title={tooltip} arrow placement='top'>
        {/* disabled なボタンは pointer-events を持たないため、ホバーを受ける要素を挟む */}
        <span style={{ display: 'inline-flex' }}>{content}</span>
      </Tooltip>
    );
  },
);

GameButton.displayName = 'GameButton';
